import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { Badge } from '../ui';
import { tg } from '../../lib/tg';
import { formatUsd } from '../../lib/format';
import type { ReferralMemberItem } from '../../lib/types';

interface Props {
  member: ReferralMemberItem;
  locale?: string;
  /** Whether this member is the current earnings filter target. */
  active?: boolean;
  /** Click — toggles the per-friend earnings filter. */
  onClick?: () => void;
}

/**
 * Friend list row. Replaces the prior `<ActivityRow />` mapping
 * because:
 *   - long usernames overflowed the row (no min-w-0 on the title)
 *   - clicking one didn't filter the earnings — now it does
 *
 * Layout: grid column for the avatar/level, flexible column for
 * name + joined date that *can shrink*, fixed column for the earned
 * amount. `truncate` only applies to the name; the date wraps below.
 */
export function MemberCard({ member, locale = 'en-US', active, onClick }: Props) {
  const { t } = useTranslation();
  const earned = Number(member.earned_from_them_usd);
  const display = member.tg_username
    ? `@${member.tg_username}`
    : member.tg_first_name || (locale.startsWith('ru') ? 'Друг' : 'Friend');
  const joined = new Date(member.joined_at).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
  });
  const Tag = onClick ? 'button' : 'div';

  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={
        onClick
          ? () => {
              tg.hapticLight();
              onClick();
            }
          : undefined
      }
      className={clsx(
        'pb-focus-ring grid w-full grid-cols-[36px_minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 text-left transition-colors',
        onClick && 'hover:bg-pb-paper2',
        active && 'bg-pb-accent-soft/40',
      )}
    >
      <span
        aria-hidden
        className={clsx(
          'flex h-9 w-9 items-center justify-center rounded-full text-[12px] font-semibold',
          member.level === 1
            ? 'bg-pb-accent-soft text-pb-accent'
            : 'bg-pb-paper2 text-tg-hint',
        )}
      >
        L{member.level}
      </span>
      <div className="min-w-0">
        <p className="truncate text-[14px] font-medium text-tg-text">
          {display}
        </p>
        <p className="pb-caption mt-0.5 truncate">
          {t('referrals.member_joined', { date: joined })}
        </p>
      </div>
      <div className="flex flex-col items-end gap-0.5">
        <span
          className={clsx(
            'pb-numeral text-[14px]',
            earned > 0 ? 'text-pb-success' : 'text-tg-hint',
          )}
        >
          {earned > 0 ? `+${formatUsd(earned)}` : t('referrals.member_earned_zero')}
        </span>
        {active ? (
          <Badge tone="accent" className="!text-[10px]">
            {t('referrals.filter_active')}
          </Badge>
        ) : null}
      </div>
    </Tag>
  );
}
